import {ReviewAction} from 'notebook/reviews/ReviewConstants';
import {processTimingEvent} from 'performance/BenchlingProfiler';
import APIUtil from 'util/APIUtil';

import {EventMetadata, ProfilerEvent, ProfilerEventDomain} from './ProfilerConstants';

const DOMAIN = ProfilerEventDomain.REVIEWS;
/* Should bump version anytime there's a change to logging events or processing them,
 * so that on Datadog, we can quickly see which custom actions have outdated logic or tags
 */
const PROFILER_REVIEWS_EVENTS_VERSION = 1;

enum ProfilerReviewsFeatures {
  SEND_FOR_REVIEW = 'sendForReview',
  REVIEW_STATUS_ACTION = 'reviewStatusAction',
}

enum ProfilerReviewsActions {
  SEND_FOR_REVIEW_STARTED = 'sendForReviewStarted',
  SEND_FOR_REVIEW_ENDED = 'sendForReviewEnded',
  REVIEW_STATUS_ACTION_STARTED = 'reviewStatusActionStarted',
  REVIEW_STATUS_ACTION_ENDED = 'reviewStatusActionEnded',
}

/**
 * Only `FINISHED` events need to be processed.
 */
const FINISHED_ACTION_TO_PROCESSING_FUNCTION: {[key in ProfilerReviewsActions]?: Function} = {
  [ProfilerReviewsActions.SEND_FOR_REVIEW_ENDED]: processSendForReview,
  [ProfilerReviewsActions.REVIEW_STATUS_ACTION_ENDED]: processReviewStatusAction,
};

export const SEND_FOR_REVIEW_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerReviewsFeatures.SEND_FOR_REVIEW,
  action: ProfilerReviewsActions.SEND_FOR_REVIEW_STARTED,
};

export const SEND_FOR_REVIEW_ENDED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerReviewsFeatures.SEND_FOR_REVIEW,
  action: ProfilerReviewsActions.SEND_FOR_REVIEW_ENDED,
};

export const REVIEW_STATUS_ACTION_STARTED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerReviewsFeatures.REVIEW_STATUS_ACTION,
  action: ProfilerReviewsActions.REVIEW_STATUS_ACTION_STARTED,
};

export const REVIEW_STATUS_ACTION_ENDED_EVENT: ProfilerEvent = {
  domain: DOMAIN,
  feature: ProfilerReviewsFeatures.REVIEW_STATUS_ACTION,
  action: ProfilerReviewsActions.REVIEW_STATUS_ACTION_ENDED,
};

export function getSendForReviewMetadata(reviewableId: string, additionalMetadata: {} = {}): EventMetadata {
  return {
    distributionMetadata: {
      ...additionalMetadata,
      model_type: APIUtil.apiIdToType(reviewableId),
    },
    actionMetadata: {
      ...additionalMetadata,
      model_type: APIUtil.apiIdToType(reviewableId),
    },
  };
}

/**
 * Mapping from a review action to action name.
 * Uses a Record to ensure any changes to the ReviewAction enum are reflected here.
 */
export const reviewActionToActionName: Record<ReviewAction, string> = {
  [ReviewAction.ACCEPT]: 'accept',
  [ReviewAction.REJECT]: 'reject',
  [ReviewAction.RETRACT]: 'retract',
  [ReviewAction.SEND_FOR_REVIEW]: 'send_for_review',
  [ReviewAction.COMMENT]: 'comment',
};

export function getReviewStatusActionMetadata(
  reviewableId: string,
  action: ReviewAction,
  additionalMetadata: {} = {}
): EventMetadata {
  return {
    distributionMetadata: {
      ...additionalMetadata,
      model_type: APIUtil.apiIdToType(reviewableId),
      action: reviewActionToActionName[action],
    },
    actionMetadata: {
      ...additionalMetadata,
      model_type: APIUtil.apiIdToType(reviewableId),
      action: reviewActionToActionName[action],
    },
  };
}

export function processReviewsEvents(profilerEvent: ProfilerEvent): void {
  FINISHED_ACTION_TO_PROCESSING_FUNCTION[profilerEvent.action as ProfilerReviewsActions]?.(profilerEvent);
}

function processSendForReview(): void {
  processTimingEvent(
    SEND_FOR_REVIEW_STARTED_EVENT,
    SEND_FOR_REVIEW_ENDED_EVENT,
    'Send For Review',
    'perf.send_for_review',
    PROFILER_REVIEWS_EVENTS_VERSION
  );
}

function processReviewStatusAction(profilerFinishedEvent: ProfilerEvent): void {
  const {id} = profilerFinishedEvent;
  processTimingEvent(
    {...REVIEW_STATUS_ACTION_STARTED_EVENT, id},
    {...REVIEW_STATUS_ACTION_ENDED_EVENT, id},
    'Review Status Action',
    'perf.review_status_action',
    PROFILER_REVIEWS_EVENTS_VERSION
  );
}
